import React, { useEffect, useState } from 'react';
import {
  Input,
  Row,
  Col,
  FormGroup,
  FormFeedback,
  Label,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
} from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { manualPunchValidationSchema, populateFormData } from './constants';
import {
  saveUpdateManualEvent,
  deleteManualEvent,
} from '../../../store/apis/eventService';
import { DateUtil } from '../../../utils';
import { TimePicker, DatePicker } from '../../Common';
import { useConfirmationModal } from '../../hooks';

import './ManualPunchIn.scss';

function ManualPunchIn({
  setPunchInOffcanvas,
  toggle,
  data,
  type = 'create',
  eventData,
  handleErrorModal,
}) {
  const queryClient = useQueryClient();
  const [eventIndex, setEventIndex] = useState(0);
  const { open, ConfirmationModal } = useConfirmationModal();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid },
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(manualPunchValidationSchema),
    mode: 'onChange',
  });

  const saveUpdateManualEventMutation = useMutation({
    mutationFn: saveUpdateManualEvent,
    onSuccess: (res, payload) => {
      if (res?.flag === 'False' && res?.errorData) {
        handleErrorModal(res);
        setPunchInOffcanvas(false);
        return;
      }
      if (payload.flag === 'insert') {
        toast.success('Manual Registration of Time submitted for approval', {
          className: 'toast-success',
        });
      } else if (payload.flag === 'update') {
        toast.success('Manual Registration of Time submitted for approval', {
          className: 'toast-success',
        });
      }
      queryClient.invalidateQueries(['getManualEventList']);
      setPunchInOffcanvas(false);
    },
    onError: error => {
      toast.error(error?.message);
    },
  });

  const deleteManualEventMutation = useMutation({
    mutationFn: deleteManualEvent,
    onSuccess: () => {
      toast.success('Manual Punch In Deleted Successfully', {
        className: 'toast-success',
      });
      queryClient.invalidateQueries(['getManualEventList']);
      setPunchInOffcanvas(false);
    },
  });

  const onSubmit = async (values, flag) => {
    const start = `${values.fromDate}T${values.fromTime}:00`;
    const end = `${values.toDate}T${values.toTime}:00`;
    const payload = {
      subject: values.subject,
      startDate: start,
      endDate: end,
      startTime: start,
      endTime: end,
      comments: values.comments,
    };

    if (type === 'create' && flag === 'insert') {
      await saveUpdateManualEventMutation.mutateAsync({
        ...payload,
        eventId: 0,
        flag,
      });
    }
    if (type === 'update' && flag === 'update') {
      await saveUpdateManualEventMutation.mutateAsync({
        ...payload,
        eventId: eventData[eventIndex].EventID,
        flag,
      });
    }
    if (type === 'update' && flag === 'delete') {
      open({
        title: 'Delete Manual Punch In Event',
        message: 'Are you sure you want to delete manual punch in event?',
        onConfirm: async () =>
          deleteManualEventMutation.mutateAsync({
            ...payload,
            eventId: eventData[eventIndex].EventID,
            flag,
          }),
        showCancelButton: true,
      });
    }
  };

  useEffect(() => {
    if (type === 'create' && data) {
      const dateStr = DateUtil.convertDdMmYyyyToHtmlDate(data.Dt);
      setValue('fromDate', dateStr);
      setValue('toDate', dateStr);
    } else if (type === 'update' && eventData && data) {
      const populated = populateFormData(eventData[eventIndex]);
      reset(populated);
    }
  }, [data, type, eventData]);

  const fromTime = watch('fromTime');
  const toTime = watch('toTime');
  useEffect(() => {
    if (fromTime || toTime) {
      trigger(['fromTime', 'toTime']);
    }
  }, [fromTime, toTime, trigger]);

  const handleNext = () => {
    setEventIndex(prevIndex => {
      const nextIndex = prevIndex + 1;
      if (nextIndex < eventData.length) {
        const populated = populateFormData(eventData[nextIndex]);
        reset(populated);
        return nextIndex;
      }
      return prevIndex;
    });
  };

  const handlePrev = () => {
    setEventIndex(prevIndex => {
      const prevIndexValue = prevIndex - 1;
      if (prevIndexValue >= 0) {
        const populated = populateFormData(eventData[prevIndexValue]);
        reset(populated);
        return prevIndexValue;
      }
      return prevIndex;
    });
  };

  return (
    <>
      <OffcanvasHeader toggle={toggle} className="mpi-header">
        <div> Manual Punch In</div>
        {eventData && eventData.length > 1 && type === 'update' && (
          <div className="d-flex justify-content-between gap-2">
            <button
              type="button"
              className="mpi-cancel py-1 px-3"
              onClick={handlePrev}
              disabled={eventIndex <= 0}
            >
              <div className="d-flex flex-row align-items-center gap-1">
                <div className="fs-6">Prev</div>
              </div>
            </button>
            <button
              type="button"
              className="custom-btn py-1 px-3"
              onClick={handleNext}
              disabled={eventIndex >= eventData.length - 1}
            >
              <div className="d-flex flex-row align-items-center gap-1">
                <div className="fs-6">Next</div>
              </div>
            </button>
          </div>
        )}
      </OffcanvasHeader>
      <OffcanvasBody style={{ minHeight: '500px', overflowY: 'auto' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="d-flex flex-wrap mt-2">
            <Col md={12} className="mb-2">
              <Label htmlFor="subject" className="my-1">
                Subject
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    className={`external-filter-input ${errors.subject ? 'is-invalid' : ''}`}
                    invalid={!!errors.subject}
                    placeholder="Enter Subject"
                  />
                )}
              />
              <FormFeedback>{errors.subject?.message}</FormFeedback>
            </Col>
            <Col md={6} className="mb-2">
              <Label className="my-1">
                From Date
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="fromDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disabled
                    className={`external-filter-input ${errors.fromDate ? 'is-invalid' : ''}`}
                    error={!!errors.fromDate}
                  />
                )}
              />
              {errors.fromDate && (
                <div className="invalid-feedback d-block">
                  {errors.fromDate.message}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-2">
              <Label className="my-1">
                To Date
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="toDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disabled
                    className={`external-filter-input ${errors.toDate ? 'is-invalid' : ''}`}
                    error={!!errors.toDate}
                  />
                )}
              />
              {errors.toDate && (
                <div className="invalid-feedback d-block">
                  {errors.toDate.message}
                </div>
              )}
            </Col>
            <Col md={6} className="my-2">
              <FormGroup check className="form-check-inline">
                <Controller
                  name="allDay"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="checkbox"
                      checked={field.value}
                      className="blue-checkbox"
                      onChange={e => {
                        const isChecked = e.target.checked;
                        field.onChange(isChecked);
                        setValue('fromTime', isChecked ? '08:00' : '');
                        setValue('toTime', isChecked ? '17:00' : '');
                        trigger(['fromTime', 'toTime']);
                      }}
                    />
                  )}
                />
                <Label className="form-check-label" htmlFor="allDay">
                  All Day
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <Row className="d-flex flex-wrap">
            <Col md={6} className="mb-2">
              <Label className="my-1">
                From Time
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="fromTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    interval={15}
                    disabled={getValues('allDay')}
                    className={`mpi-time-picker external-filter-input ${errors.fromTime ? 'is-invalid' : ''} time-picker-pointer`}
                    error={!!errors.fromTime}
                  />
                )}
              />
              {errors.fromTime && (
                <div className="invalid-feedback d-block">
                  {errors.fromTime.message}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-2">
              <Label className="my-1">
                To Time
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="toTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    interval={15}
                    disabled={getValues('allDay')}
                    className={`mpi-time-picker external-filter-input ${errors.toTime ? 'is-invalid' : ''} time-picker-pointer`}
                    error={!!errors.toTime}
                  />
                )}
              />
              {errors.toTime && (
                <div className="invalid-feedback d-block">
                  {errors.toTime.message}
                </div>
              )}
            </Col>
            <Col md={12} className="mb-3">
              <Label className="my-1">Comments</Label>
              <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="textarea"
                    placeholder="Enter Comments"
                    className={`external-filter-input ${errors.comments ? 'is-invalid' : ''}`}
                  />
                )}
              />
            </Col>
          </Row>
        </form>
      </OffcanvasBody>
      <div className="offcanvas-footer p-3">
        {type === 'create' ? (
          <Row>
            <Col md={6}>
              <Button
                type="button"
                onClick={() => setPunchInOffcanvas(false)}
                className="custom-light-btn w-100"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                type="button"
                onClick={() =>
                  handleSubmit(values => onSubmit(values, 'insert'))()
                }
                className="custom-btn w-100"
                disabled={saveUpdateManualEventMutation?.isPending || !isValid}
              >
                {saveUpdateManualEventMutation?.isPending
                  ? 'Submitting...'
                  : 'Submit'}
              </Button>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={6}>
              <Button
                type="button"
                onClick={() =>
                  handleSubmit(values => onSubmit(values, 'delete'))()
                }
                className="w-100 custom-danger-btn"
              >
                Delete
              </Button>
            </Col>
            <Col md={6}>
              <Button
                type="button"
                onClick={() =>
                  handleSubmit(values => onSubmit(values, 'update'))()
                }
                className="w-100 custom-btn"
                disabled={saveUpdateManualEventMutation?.isPending}
              >
                {saveUpdateManualEventMutation?.isPending
                  ? 'Updating...'
                  : 'Update'}
              </Button>
            </Col>
          </Row>
        )}
      </div>
      <ConfirmationModal />
    </>
  );
}

export default ManualPunchIn;
